/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect } from 'react';

import {
  type IWorkspaceMenuGroup,
  WorkspaceSubMenu as BaseWorkspaceSubMenu,
} from '@coze-foundation/space-ui-base';
import {
  type SpaceMenuKey,
  useSpaceList,
  useSpaceStore,
} from '@coze-foundation/space-store';
import { SpaceRoleType } from '@coze-arch/idl/developer_api';
import { I18n } from '@coze-arch/i18n';
import {
  IconCozBot,
  IconCozBotFill,
  IconCozChat,
  IconCozChatFill,
  IconCozClock,
  IconCozClockFill,
  IconCozDocument,
  IconCozDocumentFill,
  IconCozDatabase,
  IconCozDatabaseFill,
  IconCozDashboard,
  IconCozDashboardFill,
  IconCozFolder,
  IconCozFolderFill,
  IconCozKnowledge,
  IconCozKnowledgeFill,
  IconCozLightbulb,
  IconCozLightbulbFill,
  IconCozModel,
  IconCozModelFill,
  IconCozPeople,
  IconCozPeopleFill,
  IconCozPlugin,
  IconCozPluginFill,
  IconCozWallet,
  IconCozWalletFill,
} from '@coze-arch/coze-design/icons';
import { Space, Avatar, Typography } from '@coze-arch/coze-design';
import { useRouteConfig } from '@coze-arch/bot-hooks';

import { SpaceSubModuleEnum } from '@/const';

const harnessMenu = {
  icon: <IconCozChat />,
  activeIcon: <IconCozChatFill />,
  title: () => I18n.t('navigation_workspace_harness'),
  path: SpaceSubModuleEnum.HARNESS,
  dataTestId: 'navigation_workspace_harness',
};

const createMenuGroups = (
  canManageMembers: boolean,
): Array<IWorkspaceMenuGroup> => [
  {
    key: 'project-development',
    title: () =>
      I18n.t(
        'navigation_workspace_group_project_development',
        {},
        'Project development',
      ),
    menus: [
      {
        icon: <IconCozBot />,
        activeIcon: <IconCozBotFill />,
        title: () => I18n.t('navigation_workspace_develop', {}, 'Agents'),
        path: SpaceSubModuleEnum.DEVELOP,
        dataTestId: 'navigation_workspace_develop',
      },
      {
        icon: <IconCozFolder />,
        activeIcon: <IconCozFolderFill />,
        title: () => I18n.t('navigation_workspace_library', {}, 'Library'),
        path: SpaceSubModuleEnum.LIBRARY,
        dataTestId: 'navigation_workspace_library',
      },
      {
        icon: <IconCozKnowledge />,
        activeIcon: <IconCozKnowledgeFill />,
        title: () => I18n.t('library_resource_type_knowledge'),
        path: SpaceSubModuleEnum.KNOWLEDGE,
        dataTestId: 'navigation_workspace_knowledge',
      },
      {
        icon: <IconCozPlugin />,
        activeIcon: <IconCozPluginFill />,
        title: () => I18n.t('navigation_workspace_agent_skills'),
        path: SpaceSubModuleEnum.SKILLS,
        dataTestId: 'navigation_workspace_agent_skills',
      },
      harnessMenu,
    ],
  },
  {
    key: 'runtime-optimization',
    title: () =>
      I18n.t(
        'navigation_workspace_group_runtime_optimization',
        {},
        'Runtime optimization',
      ),
    menus: [
      {
        icon: <IconCozChat />,
        activeIcon: <IconCozChatFill />,
        title: () =>
          I18n.t(
            'navigation_workspace_prompt_development',
            {},
            'Prompt development',
          ),
        path: SpaceSubModuleEnum.PROMPT_ENGINEERING,
        dataTestId: 'navigation_workspace_prompt_engineering',
      },
      {
        icon: <IconCozModel />,
        activeIcon: <IconCozModelFill />,
        title: () => I18n.t('navigation_workspace_models'),
        path: SpaceSubModuleEnum.MODELS,
        dataTestId: 'navigation_workspace_models',
      },
      {
        icon: <IconCozDocument />,
        activeIcon: <IconCozDocumentFill />,
        title: () => I18n.t('navigation_workspace_logs'),
        path: SpaceSubModuleEnum.LOGS,
        dataTestId: 'navigation_workspace_logs',
      },
    ],
  },
  {
    key: 'evaluation',
    title: () =>
      I18n.t('navigation_workspace_group_evaluation', {}, 'Evaluation'),
    menus: [
      {
        icon: <IconCozDatabase />,
        activeIcon: <IconCozDatabaseFill />,
        title: () =>
          I18n.t('navigation_workspace_evaluation_set', {}, 'Evaluation set'),
        path: SpaceSubModuleEnum.EVALUATION_DATASETS,
        dataTestId: 'navigation_workspace_evaluation_datasets',
      },
      {
        icon: <IconCozLightbulb />,
        activeIcon: <IconCozLightbulbFill />,
        title: () => I18n.t('navigation_workspace_evaluator', {}, 'Evaluator'),
        path: SpaceSubModuleEnum.EVALUATION_EVALUATORS,
        dataTestId: 'navigation_workspace_evaluation_evaluators',
      },
      {
        icon: <IconCozDashboard />,
        activeIcon: <IconCozDashboardFill />,
        title: () =>
          I18n.t('navigation_workspace_experiment', {}, 'Experiment'),
        path: SpaceSubModuleEnum.EVALUATION_EXPERIMENTS,
        dataTestId: 'navigation_workspace_evaluation_experiments',
      },
    ],
  },
  {
    key: 'space-management',
    title: () =>
      I18n.t(
        'navigation_workspace_group_space_management',
        {},
        'Space management',
      ),
    menus: [
      {
        icon: <IconCozWallet />,
        activeIcon: <IconCozWalletFill />,
        title: () => I18n.t('navigation_workspace_billing'),
        path: SpaceSubModuleEnum.BILLING,
        dataTestId: 'navigation_workspace_billing',
      },
      ...(canManageMembers
        ? [
            {
              icon: <IconCozPeople />,
              activeIcon: <IconCozPeopleFill />,
              title: () => I18n.t('navigation_workspace_members'),
              path: SpaceSubModuleEnum.MEMBERS,
              dataTestId: 'navigation_workspace_members',
            },
            {
              icon: <IconCozClock />,
              activeIcon: <IconCozClockFill />,
              title: () => I18n.t('navigation_workspace_operation_logs'),
              path: SpaceSubModuleEnum.OPERATION_LOGS,
              dataTestId: 'navigation_workspace_operation_logs',
            },
          ]
        : []),
    ],
  },
];

const menuPermissionKeyByPath: Partial<Record<string, SpaceMenuKey>> = {
  [SpaceSubModuleEnum.DEVELOP]: 'agent',
  [SpaceSubModuleEnum.LIBRARY]: 'library',
  [SpaceSubModuleEnum.KNOWLEDGE]: 'knowledge',
  [SpaceSubModuleEnum.SKILLS]: 'skills',
  [SpaceSubModuleEnum.HARNESS]: 'harness',
  [SpaceSubModuleEnum.PROMPT_ENGINEERING]: 'prompt',
  [SpaceSubModuleEnum.EVALUATION_DATASETS]: 'evaluation',
  [SpaceSubModuleEnum.EVALUATION_EVALUATORS]: 'evaluation',
  [SpaceSubModuleEnum.EVALUATION_EXPERIMENTS]: 'evaluation',
  [SpaceSubModuleEnum.MODELS]: 'models',
  [SpaceSubModuleEnum.LOGS]: 'logs',
  [SpaceSubModuleEnum.BILLING]: 'billing',
  [SpaceSubModuleEnum.MEMBERS]: 'members',
  [SpaceSubModuleEnum.OPERATION_LOGS]: 'operation_logs',
};

export const WorkspaceSubMenu = () => {
  const { subMenuKey } = useRouteConfig();

  const currentSpace = useSpaceStore(state => state.space);
  const recentlyUsedSpaceList = useSpaceStore(
    state => state.recentlyUsedSpaceList,
  );
  const { spaces: spaceList = [], loading: spaceListLoading } = useSpaceList();
  const permissionState = useSpaceStore(state => state.menuPermissions);
  const fetchMenuPermissions = useSpaceStore(
    state => state.fetchMenuPermissions,
  );
  const currentSpaceExists = spaceList.some(
    space => space.id === currentSpace?.id,
  );
  const fallbackSpace =
    recentlyUsedSpaceList.find(recentSpace =>
      spaceList.some(space => space.id === recentSpace.id),
    ) ?? spaceList[0];
  const displaySpace = currentSpaceExists ? currentSpace : fallbackSpace;
  const displaySpaceWithRole = displaySpace as
    | {
        role_type?: SpaceRoleType;
        space_role_type?: SpaceRoleType;
      }
    | undefined;
  const displaySpaceRole = Number(
    displaySpaceWithRole?.role_type ?? displaySpaceWithRole?.space_role_type,
  );
  const canManageMembers =
    displaySpaceRole === SpaceRoleType.Owner ||
    displaySpaceRole === SpaceRoleType.Admin;

  useEffect(() => {
    // The explore route can mount this menu without passing through SpaceLayout.
    // Restore a usable current space so its workspace entries do not render null.
    if (spaceListLoading || currentSpaceExists || !fallbackSpace?.id) {
      return;
    }
    useSpaceStore.getState().setSpace(fallbackSpace.id);
  }, [currentSpaceExists, fallbackSpace?.id, spaceListLoading]);

  useEffect(() => {
    if (!displaySpace?.id) {
      return;
    }
    void fetchMenuPermissions(displaySpace.id).catch(() => undefined);
  }, [displaySpace?.id, fetchMenuPermissions]);

  const loadedPermissions =
    permissionState &&
    displaySpace?.id &&
    permissionState.space_id === displaySpace.id
      ? permissionState.menus
      : undefined;
  const menuGroups = createMenuGroups(canManageMembers)
    .map(group => ({
      ...group,
      menus: group.menus.filter(menu => {
        const permissionKey = menuPermissionKeyByPath[String(menu.path)];
        return permissionKey
          ? (loadedPermissions?.[permissionKey]?.can_view ?? false)
          : true;
      }),
    }))
    .filter(group => group.menus.length > 0);

  const headerNode = (
    <div className="cursor-pointer w-full">
      <Space
        className="h-[56px] px-[8px] w-full hover:coz-mg-secondary-hovered rounded-[10px]"
        spacing={10}
      >
        <Avatar
          className="w-[34px] h-[34px] rounded-[8px] shrink-0"
          src={displaySpace?.icon_url}
        />
        <Typography.Text
          ellipsis={{ showTooltip: true, rows: 1 }}
          className="flex-1 coz-fg-primary text-[15px] font-[600]"
        >
          {displaySpace?.name || ''}
        </Typography.Text>
      </Space>
    </div>
  );

  return (
    <BaseWorkspaceSubMenu
      header={headerNode}
      menuGroups={menuGroups}
      currentSubMenu={subMenuKey}
    />
  );
};
